import React, {useEffect, useRef} from 'react';
import AsyncSelect from 'react-select/async';
import Geocoding from '../services/Geocoding';
import Utils from '../services/Utils';

// En dessous de 3 caractères le proxy /module/geocode ne cherche pas (cf. GeocodingService.MIN_QUERY_LENGTH).
const MIN_QUERY_LENGTH = 3;
const TYPING_DELAY_MS = 600;

/**
 * Champ de recherche d'adresse : mêmes suggestions que la création de réservation manuelle (Google
 * Places si le chauffeur a configuré sa clé via Geocoding.configureGoogle, sinon le proxy TomTom).
 *
 * `value` est le libellé enregistré ; `onSelect({label, lat, lon})` n'est appelé qu'une fois les
 * coordonnées résolues, de sorte que libellé et coordonnées restent toujours cohérents.
 */
export default ({id, value, onSelect, disabled, intl}) => {
  const typingTimer = useRef(null);

  useEffect(() => () => clearTimeout(typingTimer.current), []);

  // AsyncSelect ignore d'elle-même les réponses d'une frappe dépassée : on se contente de différer l'appel.
  const loadOptions = (query, callback) => {
    clearTimeout(typingTimer.current);
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      callback([]);
      return;
    }
    typingTimer.current = setTimeout(() => {
      Geocoding.search(query).then(callback).catch(() => callback([]));
    }, TYPING_DELAY_MS);
  };

  const handleChange = async (option) => {
    const position = await Geocoding.resolve(option);
    if (position) {
      onSelect({label: option.label, lat: position.lat, lon: position.lon});
    } else {
      Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ADDRESS_NOT_RESOLVED'}));
    }
  };

  return (
    <AsyncSelect inputId={id}
                 value={value ? {label: value, value} : null}
                 loadOptions={loadOptions}
                 onChange={handleChange}
                 isDisabled={disabled}
                 filterOption={() => true}
                 placeholder={intl.formatMessage({id: 'SEARCH_AN_ADDRESS'})}
                 loadingMessage={() => intl.formatMessage({id: 'LOADING'})}
                 noOptionsMessage={({inputValue}) => intl.formatMessage({
                   id: inputValue && inputValue.trim().length >= MIN_QUERY_LENGTH ? 'NO_ADDRESS_FOUND' : 'TYPE_AN_ADDRESS'
                 })}
                 styles={Utils.getSearchableSelectStyle()}/>
  );
};
